import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import {
  addOnDemandLayer,
  type GISServiceAttributes,
  type GISService,
  GISServiceTypes,
  type GISServicesState,
  type GISStateLayer,
  gisLayerFor,
  gisSublayerFor,
  loadGISLayer,
  removeOnDemandLayer,
  selectGISLayersRaw,
  updateGISServiceAttributes,
  updateLayer,
} from '@rsos/reduxDataStore';
import { notifyDefault, notifyError } from '@rsos/utils/toastUtils';
import { normalizeServiceUrl } from '../../layerAliasMatcher';
import {
  EXTERNAL_GIS_LAYERS,
  type ExternalGisLayer,
} from '../externalGisLayers';
import { registerActionCommand } from '../registry';
import {
  getStateCatalog,
  subscribeStateCatalogUpdates,
} from '../stateLayerCatalog';
import type {
  ActionArgSuggestion,
  ActionArgSuggestionSource,
  ActionCommandDef,
  ActionContext,
  ActionExecuteResult,
  ParsedActionCommand,
} from '../types';

/**
 * Stable string -> 32-bit hash. Used to derive a deterministic
 * negative `id` for the synthetic GISService we inject for an
 * on-demand layer — negative range avoids colliding with the
 * positive int ids the PSAP `/gis-services` endpoint hands out, and
 * deterministic means re-adding the same layer collapses onto the
 * same slice entry instead of stacking duplicates.
 */
function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return h;
}

function getClickToIdentifyAttributesForUrl(
  url: string,
): Partial<GISServiceAttributes> {
  const normalized = url.toLowerCase().replace(/\/+$/, '');
  if (normalized.endsWith('/featureserver')) {
    return {
      is_click_to_identify_service: true,
      gis_service_category: 'feature_service',
    };
  }
  if (normalized.endsWith('/mapserver')) {
    return {
      is_click_to_identify_service: true,
      gis_service_category: 'map_service',
    };
  }
  return {};
}

/**
 * Build a synthetic `GISService` from the static layer descriptor.
 * The shape mirrors what `mapGISServices` in `gisServicesAPI.ts`
 * produces for backend-bootstrapped services so downstream
 * loaders / selectors / map mounting all treat it identically.
 *
 *   - `id` is a deterministic negative hash of the URL — keeps the
 *     value stable across re-adds and out of the backend's positive
 *     id space.
 *   - `gisLayerID` follows the `gis-service-<type>-<id>` convention
 *     used by `getGISLayerID`, so `LAYER_MAP` keys, `findLayerById`
 *     calls, and any debug logging line up with PSAP-bootstrapped
 *     layers.
 *   - `service_type: supplemental` (1) routes the layer through the
 *     `defaultLayerLoader`, which is what handles ArcGIS feature
 *     services like the ones in the catalog.
 *   - `attributes.supplemental_layer_category` is only set when the
 *     source row carries an explicit category. Categoryless rows are
 *     treated as session on-demand layers by the controls UI.
 */
function buildSyntheticService(layer: ExternalGisLayer): GISService {
  const id = -Math.abs(hashString(layer.url));
  const serviceType = GISServiceTypes.supplemental;
  const attributes: GISServiceAttributes = {
    ...(layer.category ? { supplemental_layer_category: layer.category } : {}),
    ...getClickToIdentifyAttributesForUrl(layer.url),
  };
  return {
    id,
    gisLayerID: `gis-service-${serviceType}-${id}`,
    service_type: serviceType,
    display_order: 99999,
    gis_service_url: layer.url,
    display_name: layer.name,
    layerIndex: 99999,
    attributes,
  };
}

/**
 * Per-row payload `getAddSuggestions` stashes in `ActionArgSuggestion.data`
 * so `executeAdd` can act on the row without re-walking the catalog.
 *
 *   - `layer` is the static descriptor from JSON.
 *   - `existingLayerId` is set when the slice already has a layer
 *     with the same (normalized) URL — `execute` uses this id
 *     directly instead of constructing a fresh synthetic service.
 *     Catches both prior `// add` injects AND PSAP-bootstrapped
 *     layers that happen to share a URL, so we never end up with
 *     two slice entries pointing at the same ArcGIS service.
 */
interface AddSuggestionData {
  layer: ExternalGisLayer;
  existingLayerId?: string;
}

interface RootStateWithPsap {
  psaps?: {
    currentPSAP?: {
      state?: string;
    };
  };
}

function readCurrentPSAPStateCode(ctx: ActionContext): string | undefined {
  const state = ctx.getState() as RootStateWithPsap;
  const rawState = state?.psaps?.currentPSAP?.state;
  if (typeof rawState !== 'string') return undefined;

  const normalized = rawState.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(normalized) ? normalized : undefined;
}

function mergeUniqueTags(
  first: readonly string[] | undefined,
  second: readonly string[] | undefined,
): string[] | undefined {
  const merged = [...(first ?? []), ...(second ?? [])]
    .map(tag => tag.trim())
    .filter(Boolean);
  if (merged.length === 0) return undefined;
  return Array.from(new Set(merged));
}

function normalizeStateTag(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  return trimmed.toUpperCase();
}

function matchesQuery(layer: ExternalGisLayer, queryLower: string): boolean {
  if (!queryLower) return true;

  const nameHit = layer.name.toLowerCase().includes(queryLower);
  const categoryHit =
    layer.category?.toLowerCase().includes(queryLower) ?? false;
  const tagHit =
    layer.tags?.some(tag => tag.toLowerCase().includes(queryLower)) ?? false;
  return nameHit || categoryHit || tagHit;
}

/**
 * Build a URL → existing GISStateLayer index from the slice
 * snapshot. Done once per `getAddSuggestions` call (rather than per
 * row) so the loop below stays O(catalog) instead of
 * O(catalog × loaded-layers).
 */
function indexExistingByUrl(ctx: ActionContext): Map<string, GISStateLayer> {
  const state = ctx.getState();
  const layers = selectGISLayersRaw(
    state as Parameters<typeof selectGISLayersRaw>[0],
  );
  const map = new Map<string, GISStateLayer>();
  for (const l of layers) {
    const url = normalizeServiceUrl(l.service?.gis_service_url);
    if (url) map.set(url, l);
  }
  return map;
}

/**
 * Live filter for `// add` autocomplete rows. Empty arg surfaces the
 * top of the configured external GIS layer catalog (capped); a typed
 * arg filters down to entries whose `name`, optional `category`, OR
 * any `tags` entry contains the typed substring case-insensitively. URL is intentionally NOT
 * matched against the typed query — every weather layer shares
 * `services9.arcgis.com`, so query-vs-URL matching would create
 * false matches across unrelated layers.
 *
 * Each row gets a `state` (`available` / `inactive` / `active`) by
 * checking whether a layer with the same normalized URL already
 * exists in the slice. The palette renders that as a small badge so
 * the user can see at a glance which layers they've already added.
 *
 * Each row also stashes its source `ExternalGisLayer` plus the
 * matched existing layer's id (if any) in `data`, so `executeAdd`
 * can route to the cheaper "just activate" path without redoing
 * the URL lookup.
 */
function getAddSuggestions(
  parsed: ParsedActionCommand,
  ctx: ActionContext,
): ActionArgSuggestion[] {
  const queryLower = parsed.rest.trim().toLowerCase();
  const existingByUrl = indexExistingByUrl(ctx);
  const psapStateCode = readCurrentPSAPStateCode(ctx);

  // Keep `layers.json` as the source catalog, then merge in state-specific
  // backend rows (if available) so both sources render in one dropdown.
  const stateCatalog = psapStateCode
    ? getStateCatalog(psapStateCode)
    : undefined;
  const stateByUrl = new Map<string, ExternalGisLayer>();
  if (stateCatalog) {
    stateCatalog.forEach(layer => {
      stateByUrl.set(normalizeServiceUrl(layer.url), layer);
    });
  }

  const internalMatches: ActionArgSuggestion[] = [];
  const stateMatches: ActionArgSuggestion[] = [];
  const internalMatchedUrls = new Set<string>();

  for (const layer of EXTERNAL_GIS_LAYERS) {
    const normalizedLayerUrl = normalizeServiceUrl(layer.url);
    const matchingStateLayer = stateByUrl.get(normalizedLayerUrl);
    const source: ActionArgSuggestionSource = matchingStateLayer
      ? 'both'
      : 'global';
    const stateTag = normalizeStateTag(matchingStateLayer?.category);
    const mergedLayer: ExternalGisLayer = {
      ...layer,
      // If backend has richer text, use it only when local source lacks it.
      description: layer.description || matchingStateLayer?.description,
      tags: mergeUniqueTags(layer.tags, matchingStateLayer?.tags),
    };
    if (!matchesQuery(mergedLayer, queryLower)) continue;

    const existing = existingByUrl.get(normalizedLayerUrl);
    const data: AddSuggestionData = {
      layer,
      existingLayerId: existing?.id,
    };
    let state: ActionArgSuggestion['state'] = 'available';
    if (existing) state = existing.active ? 'active' : 'inactive';

    internalMatches.push({
      id: mergedLayer.name,
      value: mergedLayer.name,
      label: mergedLayer.name,
      sublabel: mergedLayer.category,
      description: mergedLayer.description,
      tags: mergedLayer.tags,
      stateTag,
      state,
      source,
      data,
    });
    internalMatchedUrls.add(normalizedLayerUrl);
  }

  if (stateCatalog) {
    for (const stateLayer of stateCatalog) {
      const normalizedLayerUrl = normalizeServiceUrl(stateLayer.url);
      if (!normalizedLayerUrl || internalMatchedUrls.has(normalizedLayerUrl)) {
        continue;
      }

      const mergedLayer: ExternalGisLayer = {
        ...stateLayer,
        tags: mergeUniqueTags(stateLayer.tags, undefined),
      };
      if (!matchesQuery(mergedLayer, queryLower)) continue;

      const existing = existingByUrl.get(normalizedLayerUrl);
      const data: AddSuggestionData = {
        layer: stateLayer,
        existingLayerId: existing?.id,
      };
      let state: ActionArgSuggestion['state'] = 'available';
      if (existing) state = existing.active ? 'active' : 'inactive';

      stateMatches.push({
        id: `${mergedLayer.name}::${normalizedLayerUrl}`,
        value: mergedLayer.name,
        label: mergedLayer.name,
        // State-source rows use this category field for matching, but we
        // intentionally don't surface the raw state code as row sublabel.
        sublabel: undefined,
        description: mergedLayer.description,
        tags: mergedLayer.tags,
        stateTag: normalizeStateTag(mergedLayer.category),
        state,
        source: 'state',
        data,
      });
    }
  }

  // Keep one dropdown that combines both sources. When both sources have
  // rows, interleave them so users can see internal + state rows at once.
  // No hard cap here: host UI applies a visible-row cap, but counts and
  // filtering should still reflect the full match set.
  if (internalMatches.length > 0 && stateMatches.length > 0) {
    const merged: ActionArgSuggestion[] = [];
    let cursor = 0;
    while (cursor < internalMatches.length || cursor < stateMatches.length) {
      if (cursor < internalMatches.length) {
        merged.push(internalMatches[cursor]);
      }
      if (cursor < stateMatches.length) {
        merged.push(stateMatches[cursor]);
      }
      cursor += 1;
    }
    return merged;
  }

  if (internalMatches.length > 0) {
    return internalMatches;
  }
  return stateMatches;
}

/**
 * Look up a layer in the slice by its canonical id. Used by the
 * execute path to re-target onto an existing entry (whether
 * PSAP-bootstrapped or previously injected by `// add`) instead of
 * blindly creating a duplicate service for the same URL.
 */
function findLayerById(
  ctx: ActionContext,
  layerId: string,
): GISStateLayer | undefined {
  const state = ctx.getState();
  const layers = selectGISLayersRaw(
    state as Parameters<typeof selectGISLayersRaw>[0],
  );
  return layers.find(l => l.id === layerId);
}

/**
 * Stable toast id per layer. Used (only on the error path now) so
 * a rapid re-click on the same failing row collapses onto one
 * toast slot instead of stacking duplicates.
 */
function toastIdFor(layerName: string): string {
  return `globalmapsearch-add-${layerName}`;
}

/**
 * The `// add <layer>` execute path. Three scenarios, all funneling
 * through the same load + activate tail:
 *
 *   1. Brand-new layer (no slice entry for this URL):
 *      - Build a synthetic GISService.
 *      - Inject a placeholder via `addOnDemandLayer`.
 *      - Load + activate.
 *
 *   2. Layer already in slice but inactive (PSAP-bootstrapped OR a
 *      prior `// add`):
 *      - Reuse the existing service so we don't end up with two
 *        slice entries pointing at the same ArcGIS service.
 *      - Skip the placeholder inject; load if not loaded; activate.
 *
 *   3. Layer already loaded + active: success no-op.
 *
 * Feedback strategy is intentionally minimal:
 *   - In-flight: loading state appears in Map Layers controls on the
 *     on-demand row while `loadGISLayer` runs.
 *   - Success: a small no-icon toast ("Map layer added") that
 *     auto-dismisses fast. The map + controls panel update is the
 *     primary signal; the toast is a low-noise nudge for users
 *     whose eye is on the search bar instead of the map.
 *   - Error: a louder toast (with stable id) so the user actually
 *     sees why nothing happened. Errors need attention.
 *
 * The `Map.js` effect that watches `selectGISLayers` mounts the
 * resulting `gisLayer` onto the ESRI map automatically — no extra
 * map-side wiring needed. The same selector drives
 * `MapLayersControlWithTabs`, so the layer also appears as a
 * checkbox under its category tab.
 */
async function executeAdd(
  suggestion: ActionArgSuggestion,
  ctx: ActionContext,
): Promise<ActionExecuteResult> {
  const data = suggestion.data as AddSuggestionData | undefined;
  if (!data?.layer) {
    return { ok: false, message: 'Missing layer payload.' };
  }
  const layerName = data.layer.name;
  const toastId = toastIdFor(layerName);

  // Resolve the slice entry + service we'll act on. When the
  // suggestion was built against an already-present layer
  // (`existingLayerId`), reuse that exact service — re-deriving a
  // synthetic service for the same URL would create a duplicate
  // entry under a different id, leaving two checkboxes in the
  // controls panel. The lookup is re-done at execute time (rather
  // than relying on the cached suggestion data) so a slice clear
  // between suggestion build and click can't strand us pointing at
  // a layer that no longer exists.
  let existing: GISStateLayer | undefined;
  let service: GISService;
  if (data.existingLayerId) {
    existing = findLayerById(ctx, data.existingLayerId);
  }
  if (existing) {
    service = existing.service;
  } else {
    service = buildSyntheticService(data.layer);
    // Suggestion may have been built before the slice had this
    // layer — re-check by the synthetic id in case the user added
    // it through another path between rendering and click.
    existing = findLayerById(ctx, service.gisLayerID);
  }

  const clickToIdentifyAttributes = getClickToIdentifyAttributesForUrl(
    service.gis_service_url,
  );
  if (existing && Object.keys(clickToIdentifyAttributes).length > 0) {
    service = {
      ...service,
      attributes: {
        ...service.attributes,
        ...clickToIdentifyAttributes,
      },
    };
    ctx.dispatch(
      updateGISServiceAttributes({
        layerID: existing.id,
        attributes: clickToIdentifyAttributes,
      }),
    );
  }

  // Already on — let the host close the palette without a toast.
  // The user's last visual cue was an `On` badge on the row, so
  // any extra confirmation would be noise.
  if (existing?.loaded && existing.active) {
    return { ok: true };
  }

  // Distinguish "this is a brand-new add" from "this layer was
  // already in the slice and we're just flipping it on" so the
  // success toast can use accurate language for each. Captured
  // BEFORE the placeholder dispatch below — once `addOnDemandLayer`
  // runs, `existing` would no longer reflect the pre-click state.
  const isFreshAdd = !existing;

  if (!existing) {
    ctx.dispatch(addOnDemandLayer({ service }));
  }

  // `loadGISLayer` is a thunk; we need its `.unwrap()` to throw on
  // rejection so the catch below surfaces the failure to the user
  // instead of silently swallowing it. The thunk is declared with
  // `state: { gisServices: GISServicesState }` (see `loadGISLayer`
  // in the slice), so the dispatch cast MUST carry that same state
  // shape — anything wider (`unknown`, the app's `RootState`) would
  // leave TS unable to satisfy the thunk's `state` constraint.
  const dispatch = ctx.dispatch as unknown as ThunkDispatch<
    { gisServices: GISServicesState },
    unknown,
    UnknownAction
  >;

  try {
    if (!existing?.loaded && !existing?.loading) {
      await dispatch(loadGISLayer({ service })).unwrap();
    }

    const loaded = findLayerById(ctx, service.gisLayerID);
    if (!loaded) {
      notifyError({
        message: `Couldn't add ${layerName}`,
        options: { toastId },
      });
      return { ok: false, message: 'Layer load returned no result.' };
    }

    const gisLayer = gisLayerFor(loaded);
    if (gisLayer) {
      gisLayer.visible = true;
      const excludedIds = loaded.filteredSublayerIds || [];
      for (const sl of loaded.sublayers) {
        const gisSublayer = gisSublayerFor(sl);
        if (!gisSublayer) continue;
        gisSublayer.visible = !excludedIds.includes(sl.uniqueId);
      }
    }

    ctx.dispatch(updateLayer({ layerID: loaded.id, active: true }));
    // Minimal success affordance: no icon (so it doesn't read as a
    // "success" pat-on-the-back), no close button (it auto-dismisses
    // fast), short autoClose so it doesn't linger over the user's
    // next interaction, and slight transparency so it sits over the
    // map without fully blocking what's underneath. Same `toastId`
    // as a hypothetical follow-up error so a flapping retry doesn't
    // stack two toasts.
    //
    // Verbiage tracks the actual work done so the user gets an
    // accurate read on what just happened: a fresh add gets "<name>
    // added to map", while flipping on an existing layer gets
    // "<name> turned on".
    const successMessage = isFreshAdd
      ? `${layerName} added to map`
      : `${layerName} turned on`;
    notifyDefault({
      message: successMessage,
      noIcon: true,
      options: {
        toastId,
        autoClose: 1500,
        closeButton: false,
        style: { opacity: 0.65 },
      },
    });
    return { ok: true };
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'Failed to add layer.';
    if (isFreshAdd) {
      ctx.dispatch(removeOnDemandLayer({ layerID: service.gisLayerID }));
    }
    notifyError({
      message: isFreshAdd
        ? `Couldn't add ${layerName}: ${detail}. Removed failed on-demand layer.`
        : `Couldn't add ${layerName}: ${detail}`,
      options: { toastId },
    });
    return { ok: false, message: detail };
  }
}

const addAction: ActionCommandDef = {
  id: 'add',
  title: 'Add external GIS layer',
  description:
    'Pick an external GIS layer to load onto the map and add to the layer controls.',
  usage: '<external gis layer>',
  getArgSuggestions: getAddSuggestions,
  subscribeArgSuggestionsUpdates: subscribeStateCatalogUpdates,
  execute: executeAdd,
};

registerActionCommand(addAction);

export default addAction;
